import StringMethods from '~/utils/character'
import type { FooterLinkContext } from '~/components/footer/link-structure.vue'
import type { ProductContext } from '~/types'
import type { GroupProductByNameContext } from './types'

export const useProductsStore = defineStore('products', {
	state: () => {
		return {
			seeMoreProducts: {
				productCateory: '',
				isOpen: false
			},
			allProducts: [] as ProductContext,
			individualProducts: [] as ProductContext,
			diasporaProducts: [] as ProductContext,
			allProductCategories: [] as ProductCategoryContext,
			productCategoryGroup: {} as Record<string, ProductContext | null>,
			diasporaProductGroup: {} as GroupProductByNameContext,
			individualProductGroup: {} as GroupProductByNameContext,
			productGroup: {} as GroupProductByNameContext,
			coverPeriod: [] as string[],
			productLinks: [
				{
					label: 'FlexiCare',
					url: 'flexicare',
					category: 'health'
				},
				{
					label: 'ZenCare',
					url: 'zencare',
					category: 'health'
				},
				{
					label: 'PrimeCare',
					url: 'primecare',
					category: 'health'
				},
				{
					label: 'Comprehensive Cover',
					url: 'comprehensive-cover',
					category: 'auto'
				},
				{
					label: 'Mini Comprehensive',
					url: 'mini-comprehensive',
					category: 'auto'
				},
				{
					label: 'Third Party',
					url: 'third-party',
					category: 'auto'
				},
				{
					label: 'Gadget Cover',
					url: 'gadget',
					category: 'gadget'
				},
				{
					label: 'Travel Cover',
					url: 'travel-cover',
					category: 'travel'
				}
			],
			sendcareLinks: [
				{
					label: 'PrimeCare',
					category: 'health'
				},
				{
					label: 'PrimeCare Plus',
					category: 'health'
				},
				{
					label: 'Senior',
					category: 'health'
				},
				{
					label: 'Senior Plus',
					category: 'health'
				},
				{
					label: 'Senior Prime',
					category: 'health'
				}
			],
			pricingModal: false,
			productFilterKey: '',
			viewOnly: '' as '' | 'hospital' | 'how_it_works'
		}
	},
	getters: {
		getProductsByCategory(state) {
			return state.productCategoryGroup
		},
		getProductsByProductName(state) {
			return state.productGroup
		},
		getDiasporaProductsByName(state) {
			return state.diasporaProductGroup
		},
		getIndividualProductsByName(state) {
			return state.individualProductGroup
		},
		footerPlanLinks() {
			// Set up the footer links dynamically
			const filteredProducts = this.filteredProducts as ProductContext
			const { toCapitalize } = StringMethods()

			if (Array.isArray(filteredProducts)) {
				const categoryOrder = ['Health', 'Gadget', 'Travel', 'Auto']
				const list = filteredProducts
					.map(item => ({
						label: toCapitalize(item.name).replace(/_/g, ' ').replace('care', 'Care'),
						url: `/${String(item.form_name).replace(/_/g, '-')}`,
						category: item.productCategory.name
					}))
					.sort((a, b) => {
						return categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)
					}) as FooterLinkContext

				return list
			}
			return []
		},
		filteredProducts(state) {
			// @ts-ignore
			return filterProducts(state.allProducts) as ProductContext
		},
		sendCareCategories(state) {
			return Object.keys(state.diasporaProductGroup)
		}
	},
	actions: {
		getProductByGroupById(key: string) {
			return this.getProductsByProductName[key]
		},
		storeProductList(productList: ProductContext) {
			if (Array.isArray(productList)) {
				this.allProducts = productList
			}
		},
		storeIndividualProductList(productList: ProductContext) {
			if (Array.isArray(productList)) {
				this.individualProducts = productList
			}
		},
		storeDiasporaProductList(productList: ProductContext) {
			if (Array.isArray(productList)) {
				this.diasporaProducts = productList
			}
		},
		storeProductCategories(productCategories: ProductCategoryContext) {
			if (Array.isArray(productCategories)) {
				this.allProductCategories = productCategories
			}
		},
		storeGroupedProduct(group: Record<string, ProductContext | null>) {
			this.productCategoryGroup = group
		},
		storeGroupedProducts(list: GroupProductByNameContext) {
			this.productGroup = list
		},
		groupDiasporaProduct(list: GroupProductByNameContext) {
			this.diasporaProductGroup = list
		},
		groupIndividualProduct(list: GroupProductByNameContext) {
			this.individualProductGroup = list
		},
		setCoverPeriod(list: string[]) {
			if (Array.isArray(list)) {
				this.coverPeriod = list
			}
		},
		attachExternalLink(platform: 'business' | 'individual', link: string) {
			if (platform === 'business') {
				return `${import.meta.env.VITE_BUSINESS_URL}${link}`
			}
			if (platform === 'individual') {
				return `${import.meta.env.VITE_INDIVIDUAL_URL}${link}`
			}
		},
		navigateToProduct(productName: string | number, props?: { [id: string]: number | string }) {
			if (productName) {
				const { toLower } = StringMethods()
				const { isIndividual, sourceChannel } = useUtils()

				const allProducts = isIndividual.value ? this.individualProducts : this.allProducts
				const product = allProducts.find(
					item => item.code_name === productName || toLower(item.group_id) === toLower(productName)
				)
				if (product?.id) {
					let url = 'register'
					const group = toLower(product.group_id).trim()
					const product_category = toLower(product.productCategory.name).trim()
					let query: { [id: string]: any } = { product: toLower(product.name).replace(/ /g, '_') }

					if (!isIndividual.value) {
						if (product_category === 'health') {
							query = { ...query, page: product_category, product: group }
						} else {
							url = `register/${product.id}`
						}
					}
					if (isIndividual.value) {
						query = {
							...query,
							category: product_category,
							...(product_category === 'health' ? { group_by: group } : {})
						}
					}

					useMixpanel()?.track('web_onboarding', { product: product.name, ...props })

					navigateTo(
						{
							path: isIndividual.value
								? `${import.meta.env.VITE_INDIVIDUAL_URL}/${url}`
								: `${import.meta.env.VITE_BUSINESS_URL}/${url}`,
							query: { ...sourceChannel.value, ...query }
						},
						{ external: true, open: { target: '_blank' } }
					)
				}
			}
		},
		viewMoreProducts(category: string) {
			this.seeMoreProducts.isOpen = !this.seeMoreProducts.isOpen
			this.seeMoreProducts.productCateory = category
		},
		filterProductByCode(code: number | null) {
			const allProduct = this.allProducts
			if (code !== null && Array.isArray(allProduct)) {
				return allProduct.find(product => product.code_name === code)
			}
			return null
		},
		openPricing(viewOnly: 'hospital' | 'how_it_works' | '' = '') {
			this.pricingModal = true
			this.viewOnly = viewOnly
		}
	}
})
